/* src/views/jour/JourTachesCtrl.jsx — MES TÂCHES d'Aujourd'hui (lot 3) :
   les trois vues du proto (Ma semaine / Tout / Faites) nourries de la table
   admin_tasks. L'ancien kanban est aboli — les quatre « à jeter » de Dimitri
   sont appliqués (plus d'En cours, de swimlanes, d'étiquettes, de glisser-
   déposer). ⚠️ LES RENDEZ-VOUS de Ma semaine sont des DONNÉES D'EXEMPLE
   assumées (la jonction CalDAV viendra dans son lot) : ils sont marqués tels
   quels à l'écran, et la carte du bas dit l'état RÉEL — « pas encore
   branché », jamais « branché » sur du vide. */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { supabase } from '../../supabase'
import { todayParisIso } from '../MenageTodayView'
import Feuille from '../accueil/Feuille'
import {
  BarreVuesTaches, LigneTache, LigneRendezVous, SemaineBarres, SectionPoint,
  SectionJour, CarteCalendrier,
} from './proto/JourTachesProto'
import { FeuilleTacheEdit } from './proto/FeuillesJour'
import { MiniCalendrier } from '../accueil/ControleursFeuilles'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
// LOT 4 (décision Dimitri 2026-08-28) — la vue Notes (admin_notes) revient en
// quatrième vue : régression du lot 3 attrapée par l'inventaire lot 4.
import NotesPanel from '../../tabs/mes-taches/NotesPanel'

const Erreur = ({ texte }) => (texte
  ? <p role="alert" data-testid="taches-erreur" style={{ marginTop: 10, fontSize: 12,
      color: '#8f2f1d' }}>{texte}</p> : null)

/* ⚠️ DONNÉES D'EXEMPLE — les rendez-vous, en attendant la jonction CalDAV
   (son lot viendra). Ils sont rendus grisés « votre calendrier (exemple) »
   pour que l'écran ne mente jamais sur leur origine. */
const RDV_EXEMPLE = [
  { decalage: 1, heure: '10h00', titre: 'Rendez-vous notaire', fin: '11h30' },
  { decalage: 2, heure: '14h00', titre: 'Visite T2 Rangueil', fin: '15h00' },
  { decalage: 4, heure: '9h00', titre: 'Comptable', fin: '10h00' },
]

const JOURS_COURTS = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam']
const MOIS_LONGS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']

function isoPlus(baseIso, j) {
  const d = new Date(`${baseIso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + j)
  return d.toISOString().slice(0, 10)
}
function jourDe(iso) { return new Date(`${iso}T12:00:00Z`).getUTCDay() }
function libelleJour(iso) {
  const d = new Date(`${iso}T12:00:00Z`)
  const n = d.getUTCDate()
  return `${n === 1 ? '1er' : n} ${MOIS_LONGS[d.getUTCMonth()]}`
}
function heureDeDue(due) {
  if (!due) return null
  const m = /T(\d{2}):(\d{2})/.exec(due)
  if (!m) return null
  // Une échéance posée « sans heure » est stockée à 09:00 par l'écran
  // historique — on ne l'affiche pas comme un engagement horaire.
  const h = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit',
    hour12: false, timeZone: 'Europe/Paris' }).format(new Date(due))
  const [hh, mm] = h.split(':')
  return `${Number(hh)}h${mm}`
}

export default function JourTachesCtrl({ cfg, currentUser }) {
  const todayIso = useMemo(() => todayParisIso(), [])
  const [taches, setTaches] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [vue, setVue] = useState('semaine')
  const [feuille, setFeuille] = useState(null)   // { tache } | { neuve: true }
  const [portee, setPortee] = useState('entreprise')
  const [porteeCible, setPorteeCible] = useState(null)
  const [jourChoisi, setJourChoisi] = useState(null)
  const [heureChoisie, setHeureChoisie] = useState(null)
  const [selecteur, setSelecteur] = useState(null) // null | 'jour' | 'heure'
  const quoiRef = useRef(null)
  const precisionRef = useRef(null)

  const charger = useCallback(() => {
    supabase.from('admin_tasks')
      .select('id, title, note, status, due_at, due_all_day, archived_at, scope, logement_id, prop_id, position, done_at')
      .is('archived_at', null)
      .then(({ data, error }) => {
        if (error) {
          console.error('[jour] tâches', error.message)
          setErreur('La lecture des tâches a échoué.'); return
        }
        setTaches(data || []); setErreur(null)
      })
  }, [])
  useEffect(() => { const id = setTimeout(charger, 0); return () => clearTimeout(id) }, [charger])

  const nomLogement = useCallback((id) => {
    const l = getAllLogsFlat(cfg).find((x) => x.id === id)
    return (l && l.nom) || null
  }, [cfg])
  const porteeDe = useCallback((t) => (t.scope === 'logement' && t.logement_id
    ? nomLogement(t.logement_id) || 'Logement'
    : t.scope === 'proprietaire' ? 'Propriétaire' : 'Entreprise'), [nomLogement])

  const ouvertes = useMemo(() => (taches || [])
    .filter((t) => t.status !== 'done'), [taches])
  const faites = useMemo(() => (taches || [])
    .filter((t) => t.status === 'done'), [taches])
  const dueIso = (t) => (t.due_at ? String(t.due_at).slice(0, 10) : null)

  const cocher = useCallback(async (t) => {
    const versFait = t.status !== 'done'
    const { data, error } = await supabase.from('admin_tasks')
      .update({ status: versFait ? 'done' : 'todo',
        done_at: versFait ? new Date().toISOString() : null })
      .eq('id', t.id).select('id')
    if (error || !data || !data.length) {
      setErreur('La coche a été refusée.'); return
    }
    charger()
  }, [charger])

  const ouvrirTache = useCallback((t) => {
    setSelecteur(null)
    setFeuille({ tache: t })
    setPortee(t.scope === 'logement' ? 'logement'
      : t.scope === 'proprio' ? 'proprio' : 'entreprise')
    setPorteeCible(t.logement_id || null)
    setJourChoisi(dueIso(t))
    setHeureChoisie(t.due_all_day ? null : heureDeDue(t.due_at))
  }, [])

  const nouvelleTache = useCallback((jour = null) => {
    setSelecteur(null)
    setFeuille({ neuve: true })
    setPortee('entreprise'); setPorteeCible(null)
    setJourChoisi(jour); setHeureChoisie(null)
  }, [])

  // L'offset Paris DE CETTE DATE (été/hiver) — un « +02:00 » figé aurait
  // décalé toutes les heures d'octobre à mars (correction 27/08).
  const offsetParis = (jour) => {
    const partie = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris',
      timeZoneName: 'longOffset' }).formatToParts(new Date(`${jour}T12:00:00Z`))
      .find((x) => x.type === 'timeZoneName')
    const v = partie && partie.value.replace('UTC', '')
    return v && /^[+-]\d{2}:\d{2}$/.test(v) ? v : '+01:00'
  }

  const enregistrerTache = useCallback(async () => {
    const titre = quoiRef.current ? quoiRef.current.innerText.trim() : ''
    if (!titre) { setErreur('Dites quoi faire — le titre est vide.'); return }
    const note = precisionRef.current ? precisionRef.current.innerText.trim() : ''
    const due = jourChoisi
      ? `${jourChoisi}T${heureChoisie
        ? `${String(heureChoisie.split('h')[0]).padStart(2, '0')}:${heureChoisie.split('h')[1] || '00'}`
        : '09:00'}:00${offsetParis(jourChoisi)}`
      : null
    const ligne = {
      title: titre, note: note || null, due_at: due,
      // due_all_day : la ligne « sans heure, elle reste ici seulement » du
      // proto — le calendrier (à venir) ne posera que les heures VRAIES.
      due_all_day: !heureChoisie,
      scope: portee === 'logement' ? 'logement'
        : portee === 'proprio' ? 'proprio' : 'entreprise',
      logement_id: portee === 'logement' ? porteeCible : null,
    }
    const q = feuille && feuille.tache
      ? supabase.from('admin_tasks').update(ligne).eq('id', feuille.tache.id).select('id')
      : supabase.from('admin_tasks').insert({ ...ligne, status: 'todo' }).select('id')
    const { data, error } = await q
    if (error || !data || !data.length) {
      setErreur("L'enregistrement a été refusé."); return
    }
    setFeuille(null); charger()
  }, [feuille, jourChoisi, heureChoisie, portee, porteeCible, charger])

  /* ── MA SEMAINE : 7 jours à partir d'aujourd'hui, tâches + rendez-vous ── */
  const semaine = useMemo(() => {
    const jours = []
    for (let i = 0; i < 7; i++) {
      const iso = isoPlus(todayIso, i)
      const tachesJour = ouvertes.filter((t) => dueIso(t) === iso)
      const rdv = RDV_EXEMPLE.filter((r) => r.decalage === i)
        .map((r) => ({ ...r, iso }))
      jours.push({ iso, i, tachesJour, rdv })
    }
    return jours
  }, [todayIso, ouvertes])

  const retard = useMemo(() => ouvertes
    .filter((t) => dueIso(t) && dueIso(t) < todayIso)
    .sort((a, b) => String(a.due_at).localeCompare(String(b.due_at))), [ouvertes, todayIso])
  const sansDate = useMemo(() => ouvertes.filter((t) => !dueIso(t)), [ouvertes])
  const finSemaine = isoPlus(todayIso, 6)
  const plusTard = useMemo(() => ouvertes
    .filter((t) => dueIso(t) && dueIso(t) > finSemaine), [ouvertes, finSemaine])

    // « créée toute seule » : les tâches nées du système (la reprise « moi »
  // de l'accueil les crée avec ce titre) — aucune colonne d'origine n'existe
  // encore, le titre fait foi.
  const auto = (t) => /^Reprendre le ménage/.test(t.title || '')

  const ligneDe = (t, testId) => (
    <LigneTache key={t.id} testId={testId}
      heure={t.due_all_day ? null : heureDeDue(t.due_at)}
      faite={t.status === 'done'} barree={t.status === 'done'}
      titre={t.title}
      sous={[t.status !== 'done' && dueIso(t) && dueIso(t) < todayIso
        ? libelleJour(dueIso(t)) : (t.status === 'done' && dueIso(t) ? libelleJour(dueIso(t)) : null),
      porteeDe(t)].filter(Boolean).join(' · ')}
      sousTerra={t.status !== 'done' && dueIso(t) && dueIso(t) < todayIso}
      auto={auto(t)}
      onOuvrir={() => ouvrirTache(t)}
      onCocher={() => cocher(t)} />
  )

  const carteCalendrier = (
    <CarteCalendrier
      tag="pas encore branché" tagClasse="tag t-g"
      sous="une tâche avec une heure y apparaîtra · sans heure, elle restera ici seulement"
      note="La jonction avec vos calendriers viendra dans son lot — un calendrier « FBZ » à part, à côté des vôtres. On n'écrira jamais dans vos autres calendriers." />
  )

  return (
    <div className="page">
      <BarreVuesTaches vue={vue} onVue={setVue}
        nTout={ouvertes.length} nFaites={faites.length}
        onNouvelle={() => nouvelleTache()} />

      {taches === null && <p className="note">On lit les tâches…</p>}

      {taches !== null && vue === 'semaine' && (
        <>
          <SemaineBarres
            jours={semaine.map((j, i) => ({
              lb: i === 0 ? JOURS_COURTS[jourDe(j.iso)]
                : i === 1 ? JOURS_COURTS[jourDe(j.iso)]
                  : (libelleJour(j.iso).split(' ')[0]),
              aujourdhui: i === 0,
              barres: [
                ...j.tachesJour.map(() => (i === 0 ? '#b8412b' : '#87b8e6')),
                ...j.rdv.map(() => '#c3cdd6'),
              ].slice(0, 3),
            }))}
            onJour={() => {}} />
          {retard.length > 0 && (
            <SectionPoint point="#b8412b" titre="En retard" n={retard.length} terra
              enfants={retard.map((t) => ligneDe(t, `tache-retard-${t.id}`))} />
          )}
          {semaine.map((j, i) => {
            const cle = i === 0 ? 'Auj.' : i === 1 ? 'Dem.'
              : `${JOURS_COURTS[jourDe(j.iso)].charAt(0).toUpperCase()}${JOURS_COURTS[jourDe(j.iso)].slice(1)}.`
            const vide = j.tachesJour.length === 0 && j.rdv.length === 0
            return (
              <SectionJour key={j.iso} cle={cle} estAuj={i === 0}
                date={i === 0
                  ? `${JOURS_COURTS[jourDe(j.iso)] === 'dim' ? 'dimanche' : ''} ${libelleJour(j.iso)}`.trim()
                  : libelleJour(j.iso)}
                vide={vide}
                onNouvelle={() => nouvelleTache(j.iso)}
                enfants={vide ? null : (
                  <>
                    {j.rdv.map((r, k) => (
                      <LigneRendezVous key={`r${k}`} heure={r.heure} titre={r.titre}
                        sous={`jusqu'à ${r.fin} · votre calendrier (exemple)`} />
                    ))}
                    {j.tachesJour.map((t) => ligneDe(t, `tache-jour-${t.id}`))}
                  </>
                )} />
            )
          })}
          {sansDate.length > 0 && (
            <div style={{ marginTop: '6px', paddingTop: '14px',
              borderTop: '1px solid var(--bord)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                marginBottom: '9px' }}>
                <span style={{ flex: '1', fontSize: '12px', fontWeight: '600',
                  color: 'var(--navy-d)' }}>Sans date</span>
                <span style={{ minWidth: '20px', height: '20px', borderRadius: '10px',
                  background: '#eef2f6', color: 'var(--gris-d)', fontSize: '10px',
                  fontWeight: '600', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', padding: '0 6px' }}>{sansDate.length}</span>
              </div>
              {sansDate.map((t) => ligneDe(t, `tache-sansdate-${t.id}`))}
              <p className="note">Elles n'apparaissent dans aucun jour. C'est souvent là que traîne l'important.</p>
            </div>
          )}
          <p className="note">Vos rendez-vous viendront de votre calendrier, en lecture seule — ceux affichés sont des exemples en attendant la jonction.</p>
          {carteCalendrier}
        </>
      )}

      {taches !== null && vue === 'quand' && (
        <>
          {[['En retard', '#b8412b', retard],
            ["Aujourd'hui", '#d98c1f', ouvertes.filter((t) => dueIso(t) === todayIso)],
            ['Cette semaine', '#87b8e6', ouvertes.filter((t) => {
              const d = dueIso(t); return d && d > todayIso && d <= finSemaine
            })],
            ['Plus tard', '#c3cdd6', plusTard],
            ['Sans date', '#c3cdd6', sansDate],
          ].filter(([, , l]) => l.length > 0).map(([titre, point, l]) => (
            <SectionPoint key={titre} point={point} titre={titre} n={l.length}
              enfants={l.map((t) => ligneDe(t, `tache-tout-${t.id}`))} />
          ))}
          {ouvertes.length === 0 && <p className="note">Aucune tâche ouverte.</p>}
          {carteCalendrier}
        </>
      )}

      {taches !== null && vue === 'faites' && (
        <>
          {faites.length === 0
            ? <p className="note">Aucune tâche faite pour l'instant.</p>
            : faites.map((t) => ligneDe(t, `tache-faite-${t.id}`))}
          <p className="note">Elles restent visibles. Rien ne se supprime tout seul.</p>
          {carteCalendrier}
        </>
      )}

      {vue === 'notes' && (
        <NotesPanel cfg={cfg} />
      )}

      <Erreur texte={erreur} />

      {feuille && (
        <Feuille testId="jour-feuille-tache"
          titre={feuille.tache ? 'Une tâche' : 'Une nouvelle tâche'}
          onFermer={() => setFeuille(null)}
          enfants={
            <FeuilleTacheEdit
              quoiRef={(el) => {
                quoiRef.current = el
                if (el && feuille.tache && !el.innerText) el.innerText = feuille.tache.title || ''
              }}
              precisionRef={(el) => {
                precisionRef.current = el
                if (el && feuille.tache && !el.innerText) el.innerText = feuille.tache.note || ''
              }}
              portee={portee}
              onPortee={setPortee}
              porteeChoix={[
                { id: 'entreprise', lb: "L'entreprise" },
                { id: 'logement', lb: 'Un logement' },
                { id: 'proprio', lb: 'Un propriétaire' },
              ]}
              jourLb={jourChoisi ? libelleJour(jourChoisi) : null}
              onJour={() => setSelecteur(selecteur === 'jour' ? null : 'jour')}
              heureLb={heureChoisie === '9h00' ? null : heureChoisie}
              onHeure={() => setSelecteur(selecteur === 'heure' ? null : 'heure')}
              enfantsQuand={
                <>
                  {selecteur === 'jour' && (
                    <MiniCalendrier todayIso={todayIso} valeur={jourChoisi}
                      onPoser={(iso) => { setJourChoisi(iso); setSelecteur(null) }}
                      onVider={() => { setJourChoisi(null); setHeureChoisie(null); setSelecteur(null) }}
                      onFermer={() => setSelecteur(null)} />
                  )}
                  {selecteur === 'heure' && (
                    <div data-testid="tache-choix-heure" style={{ display: 'flex',
                      gap: '5px', flexWrap: 'wrap', padding: '9px 0' }}>
                      {Array.from({ length: 27 }, (_, k) => {
                        const h = 7 + Math.floor(k / 2)
                        const lb = `${h}h${k % 2 ? '30' : '00'}`
                        return (
                          <button key={lb} type="button"
                            data-testid={`tache-heure-${lb}`}
                            onClick={() => {
                              setHeureChoisie(lb)
                              if (!jourChoisi) setJourChoisi(todayIso)
                              setSelecteur(null)
                            }}
                            style={heureChoisie === lb
                              ? { minHeight: '36px', padding: '0 11px', borderRadius: '9px',
                                  fontSize: '11.5px', background: 'var(--navy)',
                                  border: '1px solid var(--navy)', color: '#fff',
                                  fontWeight: '600' }
                              : { minHeight: '36px', padding: '0 11px', borderRadius: '9px',
                                  fontSize: '11.5px', background: '#fff',
                                  border: '1px solid var(--bord)',
                                  color: 'var(--gris)' }}>{lb}</button>
                        )
                      })}
                      <button type="button" data-testid="tache-heure-aucune"
                        onClick={() => { setHeureChoisie(null); setSelecteur(null) }}
                        style={{ minHeight: '36px', padding: '0 11px', borderRadius: '9px',
                          fontSize: '11.5px', background: '#fff',
                          border: '1px dashed var(--bord)',
                          color: 'var(--gris-d)' }}>sans heure</button>
                    </div>
                  )}
                  {portee === 'logement' ? (
                    <div className="pastilles" data-testid="tache-choix-logement">
                      {getAllLogsFlat(cfg).map((l) => (
                        <button key={l.id} className={`ag${porteeCible === l.id ? ' on' : ''}`}
                          onClick={() => setPorteeCible(l.id)}>{l.nom}</button>
                      ))}
                    </div>
                  ) : null}
                </>
              }
              creerLb={feuille.tache ? 'Enregistrer' : 'Créer'}
              onCreer={enregistrerTache}
              onAnnuler={() => setFeuille(null)}
              erreur={<Erreur texte={erreur} />} />
          } />
      )}
    </div>
  )
}
